import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_URL } from '../../context/AuthContext';
import AdminNav from './AdminNav';

const ESTADO_STYLE = {
  completado:  { bg: '#34D39918', color: '#34D399' },
  pausada:     { bg: '#FEF3C7', color: '#F59E0B' },
  reembolsado: { bg: 'rgba(248,113,113,0.12)', color: '#F87171' },
  pendiente:   { bg: '#FCE7F3', color: '#EC4899' },
};

const ESTADO_LABEL = {
  reembolsado: 'CANCELADO',
};
const estadoLabel = (estado) => ESTADO_LABEL[estado] || estado.toUpperCase();

export default function AdminPagos() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [esMobile, setEsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const check = () => setEsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [tab, setTab]           = useState('pagos');
  const [resumen, setResumen]   = useState(null);
  const [pagos, setPagos]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  const [precio, setPrecio]         = useState('');
  const [precioActual, setPrecioActual] = useState(null);
  const [guardandoPrecio, setGuardandoPrecio] = useState(false);
  const [msgPrecio, setMsgPrecio]   = useState('');

  const [cancelando, setCancelando] = useState(null);
  const [msgCancelacion, setMsgCancelacion] = useState('');

  useEffect(() => {
    if (usuario?.rol !== 'admin') { navigate('/'); return; }
    cargarDatos();
    cargarPrecio();
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const [resRes, pagosRes] = await Promise.all([
        axios.get(`${API_URL}/admin/pagos/resumen`),
        axios.get(`${API_URL}/admin/pagos/pagos`),
      ]);
      setResumen(resRes.data);
      setPagos(pagosRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando datos');
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarPrecio = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/pagos/precio`);
      setPrecioActual(r.data.precioFormateado);
      setPrecio(r.data.precioFormateado);
    } catch {}
  };

  const guardarPrecio = async () => {
    setGuardandoPrecio(true);
    setMsgPrecio('');
    try {
      const r = await axios.put(`${API_URL}/admin/pagos/precio`, { precio });
      setPrecioActual(r.data.precioFormateado);
      setMsgPrecio('Precio actualizado. El nuevo plan PayPal se creará automáticamente en el próximo pago.');
    } catch (e) {
      setMsgPrecio(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardandoPrecio(false);
    }
  };

  const accionSuscripcion = async (pago, endpoint, confirmMsg, okMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setCancelando(pago.id);
    setMsgCancelacion('');
    try {
      await axios.post(`${API_URL}/admin/pagos/${endpoint}`, {
        subscription_id: pago.paypal_subscription_id,
        alumno_id:       pago.alumno_id,
      });
      setMsgCancelacion(okMsg);
      cargarDatos();
    } catch (e) {
      setMsgCancelacion(e.response?.data?.error || 'Error en la operación');
    } finally {
      setCancelando(null);
    }
  };

  const pausarSuscripcion = (pago) => accionSuscripcion(
    pago, 'pausar',
    `¿Pausar suscripción de ${pago.nombre}? Perderá el acceso, pero podrás reactivarla cuando quieras (no se le cobrará mientras esté pausada).`,
    `Suscripción de ${pago.nombre} pausada correctamente`,
  );

  const reactivarSuscripcion = (pago) => accionSuscripcion(
    pago, 'reactivar',
    `¿Reactivar suscripción de ${pago.nombre}? Recuperará el acceso. El próximo cobro será en su siguiente fecha de facturación (no se le cobra ahora).`,
    `Suscripción de ${pago.nombre} reactivada correctamente`,
  );

  const cancelarSuscripcion = (pago) => accionSuscripcion(
    pago, 'cancelar',
    `¿Cancelar DEFINITIVAMENTE la suscripción de ${pago.nombre}? Esta acción NO se puede deshacer: no podrás reactivarla, habría que crear una nueva.`,
    `Suscripción de ${pago.nombre} cancelada definitivamente`,
  );

  const fmt = (iso) => {
    const d = new Date(iso);
    return esMobile
      ? d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
      : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const cardStyle = { background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 12px rgba(236,72,153,0.06)' };

  const btnBase = { padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600 };
  const BotonesAccion = ({ p }) => {
    const ocupado = cancelando === p.id;
    const cur = ocupado ? 'not-allowed' : 'pointer';
    if (!p.pausable && !p.reactivable && !p.cancelable) {
      return <span style={{ color: '#FBCFE8', fontSize: '12px' }}>—</span>;
    }
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {p.pausable && (
          <button onClick={() => pausarSuscripcion(p)} disabled={ocupado}
            style={{ ...btnBase, background: '#FEF3C7', border: '1px solid #FDE68A', color: '#B45309', cursor: cur }}>
            {ocupado ? '...' : '⏸ Pausar'}
          </button>
        )}
        {p.reactivable && (
          <button onClick={() => reactivarSuscripcion(p)} disabled={ocupado}
            style={{ ...btnBase, background: '#34D39918', border: '1px solid #34D39940', color: '#059669', cursor: cur }}>
            {ocupado ? '...' : '▶ Reactivar'}
          </button>
        )}
        {p.cancelable && (
          <button onClick={() => cancelarSuscripcion(p)} disabled={ocupado}
            style={{ ...btnBase, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', cursor: cur }}>
            {ocupado ? '...' : '❌ Cancelar'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FFF0F5' }}>
      <AdminNav
        esMobile={esMobile}
        tipo="panel"
        badges={[{ label: 'PAYPAL', bg: '#FFF3E0', color: '#F59E0B', border: '#FDE68A', hideMobile: true }]}
        botonExtra={{ label: 'Panel Admin', labelMobile: '← Admin', ruta: '/admin' }}
      />

      <div className="page-content" style={{ maxWidth: '1200px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <h1 style={{ color: '#581C47', fontSize: esMobile ? '20px' : '26px', margin: 0, fontFamily: 'Inter, sans-serif' }}>
            {esMobile ? '💳 Pagos' : 'Panel de Pagos PayPal'}
          </h1>
          <button onClick={cargarDatos} style={{ background: '#FCE7F3', border: '1px solid #FBCFE8', color: '#EC4899', padding: '5px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
            ↻ Actualizar
          </button>
        </div>
        <p style={{ color: '#BE84A3', marginBottom: '24px', fontSize: '13px' }}>Control de suscripciones, cancelaciones y configuración</p>

        {/* Tarjetas resumen */}
        {resumen && (
          <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '10px', marginBottom: '24px' }}>
            {[
              { label: 'Este mes',      valor: `${resumen.ingresosMes?.toFixed(2)}€`,  icon: '📈', color: '#DB2777' },
              { label: 'Total cobrado', valor: `${resumen.ingresosTotal?.toFixed(2)}€`, icon: '💶', color: '#EC4899' },
              { label: 'Completados',   valor: resumen.totalCompletados,                icon: '✅', color: '#34D399' },
              { label: 'Cancelados',    valor: resumen.totalReembolsados,               icon: '❌', color: '#F87171' },
            ].map(item => (
              <div key={item.label} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: esMobile ? '14px' : '16px 20px', boxShadow: '0 2px 8px rgba(236,72,153,0.05)' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{item.icon}</div>
                <div style={{ fontSize: esMobile ? '18px' : '20px', fontWeight: 800, color: item.color, marginBottom: '2px', fontFamily: 'monospace' }}>{item.valor}</div>
                <div style={{ color: '#BE84A3', fontSize: '11px' }}>{item.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '4px', boxShadow: '0 2px 8px rgba(236,72,153,0.06)', width: esMobile ? '100%' : 'fit-content' }}>
          {[{ id: 'pagos', label: '💳 Suscripciones' }, { id: 'config', label: '⚙️ Config' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' : 'transparent',
              border: 'none', color: tab === t.id ? '#fff' : '#BE84A3',
              padding: '8px 20px', borderRadius: '9px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
              flex: esMobile ? 1 : undefined,
              boxShadow: tab === t.id ? '0 4px 12px rgba(236,72,153,0.3)' : 'none',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#F87171', fontSize: '13px' }}>{error}</div>}
        {msgCancelacion && (
          <div style={{ background: msgCancelacion.includes('Error') ? 'rgba(248,113,113,0.08)' : '#34D39912', border: `1px solid ${msgCancelacion.includes('Error') ? 'rgba(248,113,113,0.3)' : '#34D39940'}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: msgCancelacion.includes('Error') ? '#F87171' : '#34D399', fontSize: '13px' }}>
            {msgCancelacion}
          </div>
        )}

        {/* TAB: SUSCRIPCIONES */}
        {tab === 'pagos' && (
          <>
            {cargando ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#BE84A3' }}>Cargando suscripciones...</div>
            ) : pagos.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '48px', color: '#BE84A3' }}>No hay pagos registrados</div>
            ) : esMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {pagos.map(p => {
                  const est = ESTADO_STYLE[p.estado] || ESTADO_STYLE.pendiente;
                  return (
                    <div key={p.id} style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 2px 8px rgba(236,72,153,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ color: '#581C47', fontSize: '14px', fontWeight: 500, marginBottom: '2px' }}>{p.nombre}</p>
                          <p style={{ color: '#BE84A3', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.alumno_email}</p>
                        </div>
                        <span style={{ color: '#34D399', fontSize: '16px', fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>
                          {p.monto?.toFixed(2)}€/mes
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ background: est.bg, color: est.color, borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700 }}>{estadoLabel(p.estado)}</span>
                          <span style={{ color: '#BE84A3', fontSize: '11px' }}>{fmt(p.fecha)}</span>
                        </div>
                        <BotonesAccion p={p} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="table-scroll">
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #FBCFE8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FFF0F5' }}>
                  <span style={{ color: '#581C47', fontWeight: 600, fontSize: '14px' }}>Suscripciones ({pagos.length})</span>
                  <span style={{ color: '#BE84A3', fontSize: '12px' }}>Gestionadas con PayPal</span>
                </div>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #FBCFE8', background: '#FFF0F5' }}>
                      {['Cliente', 'Importe/mes', 'Estado', 'Fecha', 'Acciones'].map(h => (
                        <th key={h} style={{ padding: '11px 16px', color: '#BE84A3', fontSize: '11px', textAlign: 'left', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.map((p, i) => {
                      const est = ESTADO_STYLE[p.estado] || ESTADO_STYLE.pendiente;
                      return (
                        <tr key={p.id} style={{ borderBottom: i < pagos.length - 1 ? '1px solid #FCE7F3' : 'none' }}>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ color: '#581C47', fontSize: '13px', fontWeight: 500 }}>{p.nombre}</div>
                            <div style={{ color: '#BE84A3', fontSize: '11px', marginTop: '2px' }}>{p.alumno_email}</div>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#34D399', fontSize: '14px', fontWeight: 700, fontFamily: 'monospace' }}>
                            {p.monto?.toFixed(2)}€
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: est.bg, color: est.color, borderRadius: '6px', padding: '3px 10px', fontSize: '11px', fontWeight: 700 }}>
                              {estadoLabel(p.estado)}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#BE84A3', fontSize: '12px' }}>{fmt(p.fecha)}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <BotonesAccion p={p} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* TAB: CONFIGURACIÓN */}
        {tab === 'config' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: esMobile ? '100%' : '520px' }}>
            <div style={cardStyle}>
              <h2 style={{ color: '#581C47', fontSize: '16px', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>Precio de suscripción</h2>
              <p style={{ color: '#BE84A3', fontSize: '13px', marginBottom: '16px' }}>
                Precio actual: <strong style={{ color: '#EC4899' }}>{precioActual}€/mes</strong>
              </p>
              <div style={{ display: 'flex', flexDirection: esMobile ? 'column' : 'row', gap: '10px', alignItems: esMobile ? 'stretch' : 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', color: '#BE84A3', fontSize: '12px', marginBottom: '6px' }}>Nuevo precio (€/mes)</label>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#FFF0F5', border: '1px solid #FBCFE8', borderRadius: '10px', overflow: 'hidden' }}>
                    <span style={{ padding: '0 12px', color: '#EC4899', fontSize: '16px', borderRight: '1px solid #FBCFE8', fontWeight: 600 }}>€</span>
                    <input
                      type="number" min="1" step="0.01" value={precio}
                      onChange={e => setPrecio(e.target.value)}
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#581C47', padding: '10px 14px', fontSize: '15px', fontFamily: 'monospace' }}
                      placeholder="60.00"
                    />
                  </div>
                </div>
                <button
                  onClick={guardarPrecio} disabled={guardandoPrecio}
                  style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: '11px 20px', borderRadius: '50px', cursor: guardandoPrecio ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600, opacity: guardandoPrecio ? 0.6 : 1, whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(236,72,153,0.35)' }}
                >
                  {guardandoPrecio ? 'Guardando...' : 'Guardar precio'}
                </button>
              </div>
              {msgPrecio && <p style={{ marginTop: '12px', color: msgPrecio.includes('Error') ? '#F87171' : '#34D399', fontSize: '13px' }}>{msgPrecio}</p>}
            </div>

            <div style={cardStyle}>
              <h2 style={{ color: '#581C47', fontSize: '16px', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>Variables de entorno PayPal</h2>
              <p style={{ color: '#BE84A3', fontSize: '13px', marginBottom: '16px' }}>
                Configura en <code style={{ background: '#FFF0F5', padding: '2px 6px', borderRadius: '4px', color: '#EC4899', border: '1px solid #FBCFE8' }}>backend/.env</code>
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'PAYPAL_CLIENT_ID', desc: 'Client ID de tu app PayPal' },
                  { label: 'PAYPAL_SECRET',    desc: 'Secret de tu app PayPal' },
                  { label: 'PAYPAL_ENV',       desc: '"sandbox" para pruebas / "production" para producción' },
                  { label: 'PRECIO_ACCESO',    desc: 'Precio mensual en euros (ej: 60)' },
                ].map(v => (
                  <div key={v.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', background: '#FFF0F5', borderRadius: '10px', border: '1px solid #FBCFE8' }}>
                    <div style={{ minWidth: 0 }}>
                      <code style={{ color: '#EC4899', fontSize: '12px', wordBreak: 'break-all' }}>{v.label}</code>
                      <p style={{ color: '#BE84A3', fontSize: '11px', margin: 0, marginTop: '2px' }}>{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
